import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import LearningsTab from './LearningsTab';
import DailyReport from './DailyReport';
import VisualInspectTab from './VisualInspectTab';
const TerminalDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [currentData, setCurrentData] = useState({
    temperature: null,
    humidity: null,
    vpd: null,
    fanSpeed: null,
    lightLevel: null,
    co2: null,
    externalTemp: null,
    externalHumidity: null,
    timestamp: new Date()
  });
  
  const [allPortsData, setAllPortsData] = useState({});
  const [portConfig, setPortConfig] = useState({ ports: [] });
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [growInfo, setGrowInfo] = useState({
    stage: 'vegetative',
    day: 0,
    aiEnabled: false,
    controllerConnected: false,
    strain: 'Unknown'
  });
  const [historyData, setHistoryData] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isConnected, setIsConnected] = useState(false);
  const [visualInspectionData, setVisualInspectionData] = useState(null);

 const YOUTUBE_LIVE_ID = "mR3K1M1L6Ns";
  const API_URL = process.env.REACT_APP_API_URL || 'https://cannaclaude-backend.onrender.com';

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/public/status`);
      
      if (!response.ok) {
        throw new Error('Server not responding');
      }
      
      const data = await response.json();
      
      if (data.sensors) {
        setCurrentData({
          temperature: data.sensors.temperature,
          humidity: data.sensors.humidity,
          vpd: data.sensors.vpd,
          fanSpeed: data.sensors.fanSpeed,
          lightLevel: data.sensors.lightIntensity,
          co2: data.sensors.co2,
          externalTemp: data.sensors.externalTemp,
          externalHumidity: data.sensors.externalHumidity,
          timestamp: data.sensors.timestamp ? new Date(data.sensors.timestamp) : new Date()
        });

        
        setHistoryData(prev => {
          const newPoint = {
            time: new Date().toLocaleTimeString(),
            temp: data.sensors.temperature ? parseFloat(data.sensors.temperature.toFixed(1)) : null,
            humidity: data.sensors.humidity ? parseFloat(data.sensors.humidity.toFixed(1)) : null,
            vpd: data.sensors.vpd ? parseFloat(data.sensors.vpd.toFixed(2)) : null
          };
          return [...prev, newPoint].slice(-20);
        });
      }
      
      setAiAnalysis(data.aiAnalysis);
      setDecisions(data.recentDecisions || []);
      
      //  EXTRACT VISUAL INSPECTION DATA
      if (data.visualInspection) {
        setVisualInspectionData(data.visualInspection);
      }
      
      setGrowInfo({
        stage: data.currentStage || 'vegetative',
        day: data.currentDay || 0,
        aiEnabled: data.config?.aiEnabled || false,
        aiAutoControl: data.config?.aiAutoControl || false,
        aiControlMode: data.config?.aiControlMode || 'manual',
        controllerConnected: data.config?.controllerConnected || false,
        strain: data.strain || 'Unknown'
      });
      
      setLastUpdate(new Date());
      setIsConnected(true);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsConnected(false);
      simulateData();
    }
  };

  const fetchAllPorts = async () => {
    try {
      const response = await fetch(`${API_URL}/public/sensors/all`);
      if (response.ok) {
        const data = await response.json();
        setAllPortsData(data.ports || {});
      }
    } catch (error) {
      console.error('Error fetching all ports:', error);
    }
  };

  const fetchPortConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/public/ports/config`);
      if (response.ok) {
        const data = await response.json();
        setPortConfig(data);
      }
    } catch (error) {
      console.error('Error fetching port config:', error);
    }
  };

  const simulateData = () => {
    setCurrentData(prev => ({
      ...prev,
      temperature: prev.temperature ? prev.temperature + (Math.random() - 0.5) * 1.5 : 74.2,
      humidity: prev.humidity ? Math.max(45, Math.min(70, prev.humidity + (Math.random() - 0.5) * 2)) : 60.1,
      vpd: prev.vpd ? prev.vpd + (Math.random() - 0.5) * 0.1 : 0.17,
      fanSpeed: prev.fanSpeed || null,
      timestamp: new Date()
    }));
    
    setHistoryData(prev => {
      const newPoint = {
        time: new Date().toLocaleTimeString(),
        temp: currentData.temperature?.toFixed(1) || 74.2,
        humidity: currentData.humidity?.toFixed(1) || 60.1,
        vpd: currentData.vpd?.toFixed(2) || 0.17
      };
      return [...prev, newPoint].slice(-20);
    });
    
    setLastUpdate(new Date());
  };

  useEffect(() => {
    fetchData();
    fetchAllPorts();
    fetchPortConfig();
    
    const interval = setInterval(() => {
      fetchData();
      fetchAllPorts();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: true 
    }).toUpperCase();
  };

  const MetricCard = ({ title, value, unit }) => (
    <div className="metric-card rounded p-3">
      <div className="text-xs font-bold mb-1 text-[#00ff4180]">{title}</div>
      <div className="text-3xl font-bold terminal-glow">
        {value !== null && value !== undefined ? (typeof value === 'number' ? value.toFixed(value < 10 ? 2 : 1) : value) : '--'}
      </div>
      <div className="text-xs text-[#00ff4180] mt-1">{unit}</div>
    </div>
  );

  const EquipmentCard = ({ portNum, deviceName, deviceType, speed, mode }) => {
    const getDeviceIcon = (type) => {
      switch(type) {
        case 'exhaust_fan':
          return '💨';
        case 'circulation_fan':
          return '🌀';
        case 'humidifier':
          return '💧';
        case 'dehumidifier':
          return '🔥';
        case 'heater':
          return '🌡️';
        case 'light':
          return '💡';
        default:
          return '⚙️';
      }
    };

    const speedPercent = speed !== null && speed !== undefined ? (speed / 10) * 100 : 0;

    return (
      <div className="metric-card rounded p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold text-[#00ff4180]">
            {getDeviceIcon(deviceType)} PORT {portNum}
          </div>
          <div className="text-[10px] px-2 py-1 rounded bg-[#00ff4120] text-[#00ff41]">
            {mode || 'ACTIVE'}
          </div>
        </div>
        <div className="text-sm font-bold mb-2">{deviceName}</div>
        <div className="flex items-center justify-between">
          <div className="text-xs text-[#00ff4180]">POWER</div>
          <div className="text-xl font-bold terminal-glow">
            {speed !== null && speed !== undefined ? `${speed}/10` : '--'}
          </div>
        </div>
        {/* Power Level Bar */}
        <div className="mt-2 h-2 bg-[#00ff4120] rounded-full overflow-hidden">
          <div 
            className="h-full bg-[#00ff41] transition-all duration-500"
            style={{ width: `${speedPercent}%` }}
          />
        </div>
        <div className="text-[10px] text-[#00ff4180] mt-1 text-right">
          {speedPercent.toFixed(0)}%
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-[#00ff41] p-6 font-mono">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap');
        
        * {
          font-family: 'Share Tech Mono', monospace;
        }
        
        .terminal-border {
          border: 2px solid #00ff41;
          box-shadow: 0 0 10px rgba(0, 255, 65, 0.3), inset 0 0 10px rgba(0, 255, 65, 0.1);
        }
        
        .terminal-glow {
          text-shadow: 0 0 10px rgba(0, 255, 65, 0.5);
        }
        
        .scan-line {
          position: relative;
          overflow: hidden;
        }
        
        .scan-line::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(0, 255, 65, 0.1) 50%,
            transparent 100%
          );
          animation: scan 8s linear infinite;
          pointer-events: none;
        }
        
        @keyframes scan {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        
        .blink {
          animation: blink 1s step-start infinite;
        }
        
        @keyframes blink {
          50% { opacity: 0; }
        }
        
        .metric-card {
          background: rgba(0, 255, 65, 0.05);
          border: 1px solid #00ff41;
          transition: all 0.3s ease;
        }
        
        .metric-card:hover {
          background: rgba(0, 255, 65, 0.1);
          box-shadow: 0 0 20px rgba(0, 255, 65, 0.3);
        }
        
        .priority-high {
          background: rgba(255, 0, 0, 0.2);
          border-color: #ff0000;
          color: #ff0000;
        }
        
        .priority-medium {
          background: rgba(255, 165, 0, 0.2);
          border-color: #ffa500;
          color: #ffa500;
        }
      `}</style>

   {/* Header with Integrated Grow Info */}
<div className="terminal-border rounded-lg p-6 mb-6 scan-line">
  <div className="flex justify-between items-start">
    {/* Left: Title and Grow Info */}
    <div>
      <h1 className="text-4xl font-bold terminal-glow mb-3">CannaClaude</h1>
      <div className="space-y-1.5 text-sm">
        {/* Row 1: Strain, Day, Stage */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-[#00ff4180] text-xs">STRAIN:</span>
            <span className="font-bold uppercase">{growInfo?.strain || 'UNKNOWN'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#00ff4180] text-xs">DAY:</span>
            <span className="font-bold">{growInfo?.day || '--'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#00ff4180] text-xs">STAGE:</span>
            <span className="font-bold uppercase">{growInfo?.stage || 'UNKNOWN'}</span>
          </div>
        </div>
        {/* Row 2: AI Control */}
        <div className="flex items-center gap-2">
          <span className="text-[#00ff4180] text-xs">AI CONTROL:</span>
          <span className="font-bold">
            {growInfo?.aiAutoControl ? `✓ ${growInfo.aiControlMode?.toUpperCase()}` : '✗ MANUAL'}
          </span>
        </div>
        {/* Row 3: Controller */}
        <div className="flex items-center gap-2">
          <span className="text-[#00ff4180] text-xs">CONTROLLER:</span>
          <span className="font-bold">
            {growInfo?.controllerConnected ? '✓ CONNECTED' : '✗ SIMULATION'}
          </span>
        </div>
      </div>
    </div>
    
    {/* Center: Contact Address */}
    <div className="flex-1 flex flex-col items-center justify-center px-4">
      <div className="text-xs text-[#00ff4180] mb-2">CONTACT ADDRESS</div>
      <div className="text-sm font-mono terminal-glow break-all text-center max-w-md">
        2a2yNkuKxiQiv8Gek2MKihMYqrTxV3AUS233yeW6pump
      </div>
    </div>
    
    {/* Right: System Time */}
    <div className="text-right">
      <div className="text-xs text-[#00ff4180] mb-1">SYSTEM TIME</div>
      <div className="text-3xl font-bold terminal-glow">
        {formatTime(lastUpdate)}
      </div>
      <div className="text-xs text-[#00ff4180] mt-2">
        LAST UPDATE: {lastUpdate.toLocaleTimeString()}
      </div>
    </div>
  </div>
</div>
      
{/* Tab Navigation */}
      <div className="terminal-border rounded-lg p-2 mb-6 flex gap-2">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`flex-1 px-4 py-3 rounded font-bold transition-all ${
            activeTab === 'dashboard'
              ? 'bg-[#00ff41] text-[#0a0e1a]'
              : 'bg-[#00ff4120] text-[#00ff41] hover:bg-[#00ff4130]'
          }`}
        >
           DASHBOARD
        </button>
        <button
          onClick={() => setActiveTab('visual')}
          className={`flex-1 px-4 py-3 rounded font-bold transition-all ${
            activeTab === 'visual'
              ? 'text-white'
              : 'text-[#00ff41] hover:bg-[#00ff4130]'
          }`}
          style={activeTab === 'visual'
            ? { backgroundColor: '#9c27b0' }
            : { backgroundColor: 'rgba(156, 39, 176, 0.15)' }}
        >
           🔍 VISUAL INSPECT
        </button>
        <button
          onClick={() => setActiveTab('report')}
          className={`flex-1 px-4 py-3 rounded font-bold transition-all ${
            activeTab === 'report'
              ? 'bg-[#00ff41] text-[#0a0e1a]'
              : 'bg-[#00ff4120] text-[#00ff41] hover:bg-[#00ff4130]'
          }`}
        >
           DAILY REPORT
        </button>
        <button
          onClick={() => setActiveTab('learnings')}
          className={`flex-1 px-4 py-3 rounded font-bold transition-all ${
            activeTab === 'learnings'
              ? 'bg-[#00ff41] text-[#0a0e1a]'
              : 'bg-[#00ff4120] text-[#00ff41] hover:bg-[#00ff4130]'
          }`}
        >
           LEARNINGS
        </button>
      </div>
      {/* DASHBOARD TAB CONTENT */}
      {activeTab === 'dashboard' && (
        <>
          {/* Main Content Grid - 3 Column Layout */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            
            {/* LEFT COLUMN - Environmental Data & Equipment */}
            <div className="xl:col-span-3 space-y-6">
              
              {/* Current Conditions */}
              <div className="terminal-border rounded-lg p-4 scan-line">
                <h2 className="text-lg font-bold mb-3 flex items-center gap-2 terminal-glow">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  ENVIRONMENT
                </h2>
                <div className="space-y-3">
                  <MetricCard
                    title="TEMP"
                    value={currentData.temperature}
                    unit="°F"
                  />
                  <MetricCard
                    title="HUMIDITY"
                    value={currentData.humidity}
                    unit="%"
                  />
                  <MetricCard
                    title="VPD"
                    value={currentData.vpd}
                    unit="kPa"
                  />
                </div>
              </div>

              {/* Equipment Status - All Ports */}
              <div className="terminal-border rounded-lg p-4 scan-line">
                <h2 className="text-lg font-bold mb-3 flex items-center gap-2 terminal-glow">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                  EQUIPMENT
                </h2>
                <div className="space-y-3">
                  {portConfig.ports && portConfig.ports.length > 0 ? (
                    portConfig.ports.map(port => {
                      const portData = allPortsData[`port_${port.portNumber}`];
                      return (
                        <EquipmentCard
                          key={port.portNumber}
                          portNum={port.portNumber}
                          deviceName={port.deviceName}
                          deviceType={port.deviceType}
                          speed={portData?.fanSpeed}
                          mode={portData?.mode || (portData?.fanSpeed > 0 ? 'ON' : 'OFF')}
                        />
                      );
                    })
                  ) : (
                    <div className="text-xs text-[#00ff4180] text-center py-4">
                      No equipment configured
                    </div>
                  )}
                </div>
              </div>

             {/* Light, CO2 & Room Sensors */}
              {(currentData.lightLevel != null || currentData.co2 != null || currentData.externalTemp != null) && (
                <div className="terminal-border rounded-lg p-4 scan-line">
                  <h2 className="text-lg font-bold mb-3 terminal-glow">SENSORS</h2>
                  <div className="space-y-3">
                    {currentData.lightLevel != null && (
                      <MetricCard
                        title="💡 LIGHT"
                        value={currentData.lightLevel}
                        unit="LUX"
                      />
                    )}
                    {currentData.co2 != null && (
                      <MetricCard
                        title="🌿 CO2"
                        value={currentData.co2}
                        unit="PPM"
                      />
                    )}
                    {currentData.externalTemp != null && (
                      <MetricCard
                        title="🏠 ROOM TEMP"
                        value={currentData.externalTemp}
                        unit="°F"
                      />
                    )}
                    {currentData.externalHumidity != null && (
                      <MetricCard
                        title="🏠 ROOM RH"
                        value={currentData.externalHumidity}
                        unit="%"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* VPD Indicator */}
              {currentData.vpd !== null && currentData.vpd !== undefined && (
                <div className="terminal-border rounded-lg p-4">
                  <h2 className="text-lg font-bold mb-2 terminal-glow">VPD STATUS</h2>
                  <p className="text-xs text-[#00ff4180] mb-3">CURRENT: {currentData.vpd.toFixed(2)} kPa</p>
                  <div className="relative h-12 bg-gradient-to-r from-blue-500 via-[#00ff41] to-red-500 rounded border border-[#00ff41]">
                    <div className="absolute top-0 left-0 w-full h-full flex items-center justify-between px-2 text-[10px] font-bold text-black">
                      <span>LOW</span>
                      <span>SEED</span>
                      <span>VEG</span>
                      <span>FLOWER</span>
                      <span>HIGH</span>
                    </div>
                    <div 
                      className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-[#00ff41] border-4 border-[#0a0e1a] rounded-full shadow-lg transition-all duration-500"
                      style={{ 
                        left: `${Math.min(95, Math.max(2, (currentData.vpd / 2) * 100))}%`,
                        boxShadow: '0 0 15px rgba(0, 255, 65, 0.8)'
                      }}
                    />
                  </div>
                </div>
              )}

            </div>

            {/* CENTER COLUMN - Visual Inspection & YouTube Live Stream */}
            <div className="xl:col-span-6 space-y-6">
              
              {/*  VISUAL INSPECTION SUMMARY - ABOVE STREAM */}
              {visualInspectionData && visualInspectionData.lastInspection && (
                <div className="terminal-border rounded-lg overflow-hidden scan-line" style={{ borderColor: '#9c27b0' }}>
                  <div className="px-4 py-2 flex items-center gap-2" style={{ backgroundColor: '#9c27b0', color: 'white' }}>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                    </svg>
                    <h2 className="font-bold">VISUAL INSPECTION</h2>
                  </div>
                  
                  <div className="p-4 bg-[#0a0e1a]">
                    <div className="bg-[#9c27b020] border border-[#9c27b0] rounded p-3 mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs" style={{ color: '#9c27b0' }}>PLANT HEALTH</span>
                        <span className="text-2xl font-bold terminal-glow">
                          {visualInspectionData.lastInspection.healthScore}%
                        </span>
                      </div>
                      <div className="h-2 bg-[#00ff4120] rounded-full overflow-hidden">
                        <div 
                          className="h-full transition-all duration-500"
                          style={{ 
                            width: `${visualInspectionData.lastInspection.healthScore}%`,
                            background: `linear-gradient(to right, #f44336, #ffeb3b, #00ff41)`
                          }}
                        />
                      </div>
                    </div>

                    <div className="bg-[#9c27b020] border border-[#9c27b0] rounded p-3 mb-3">
                      <p className="text-xs leading-relaxed">
                        {visualInspectionData.lastInspection.summary}
                      </p>
                    </div>

                    {visualInspectionData.lastInspection.issuesDetected && (
                      <div className="bg-[#ffa50030] border border-[#ffa500] rounded p-3 mb-3">
                        <h3 className="font-bold text-xs mb-1 text-[#ffa500]">⚠ ISSUES DETECTED</h3>
                        <p className="text-xs">Check visual inspection tasks below</p>
                      </div>
                    )}

                    <div className="text-[10px] text-[#00ff4180] text-center mt-2">
                      Last inspection: {new Date(visualInspectionData.lastInspection.timestamp).toLocaleString()}
                    </div>
                  </div>
                </div>
              )}
              
              {/* YouTube Live Embed */}
              <div className="terminal-border rounded-lg overflow-hidden">
                <div className="bg-[#00ff41] text-[#0a0e1a] px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 font-bold text-lg">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                    </svg>
                    LIVE GROW CAM
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-600 blink"></span>
                    <span className="font-bold">LIVE</span>
                  </div>
                </div>
                <div className="relative pb-[56.25%] bg-black">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${YOUTUBE_LIVE_ID}?autoplay=1&mute=1`}
                    title="ClaudeX"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>

              {/* Historical Chart */}
              {historyData.length > 0 && (
                <div className="terminal-border rounded-lg p-4 scan-line">
                  <h2 className="text-lg font-bold mb-3 flex items-center gap-2 terminal-glow">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    ENVIRONMENTAL TRENDS
                  </h2>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={historyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#00ff4140" />
                      <XAxis dataKey="time" tick={{ fontSize: 11, fill: '#00ff41' }} stroke="#00ff41" />
                      <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#00ff41' }} stroke="#00ff41" />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#00ff41' }} stroke="#00ff41" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#0a0e1a', 
                          border: '1px solid #00ff41',
                          color: '#00ff41',
                          fontFamily: 'Share Tech Mono, monospace'
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px', color: '#00ff41' }} />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="temp" 
                        stroke="#00ff41" 
                        name="Temp (°F)" 
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="humidity" 
                        stroke="#00ff4180" 
                        name="Humidity (%)" 
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="vpd" 
                        stroke="#00ff4160" 
                        name="VPD (kPa)" 
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

            </div>

            {/* RIGHT COLUMN - AI Analysis & Decisions */}
            <div className="xl:col-span-3 space-y-6">
              
              {/* AI Analysis */}
              {aiAnalysis && (
                <div className="terminal-border rounded-lg overflow-hidden scan-line">
                  <div className="bg-[#00ff41] text-[#0a0e1a] px-4 py-2 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z" />
                    </svg>
                    <h2 className="font-bold">CLAUDE AI</h2>
                  </div>
                  
                  <div className="p-4 bg-[#0a0e1a]">
                    <div className="bg-[#00ff4110] border border-[#00ff41] rounded p-3 mb-3">
                      <p className="text-sm leading-relaxed">{aiAnalysis.analysis || 'Analyzing conditions...'}</p>
                    </div>
                    
                    {aiAnalysis.environmentalStatus && (
                      <div className="bg-[#00ff4110] border border-[#00ff41] rounded p-3 mb-3">
                        <h3 className="font-bold text-sm mb-2">STATUS:</h3>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <div className="text-[#00ff4180]">OVERALL</div>
                            <div className="font-bold uppercase">{aiAnalysis.environmentalStatus.overall}</div>
                          </div>
                          <div>
                            <div className="text-[#00ff4180]">TEMP</div>
                            <div className="font-bold uppercase">{aiAnalysis.environmentalStatus.tempStatus}</div>
                          </div>
                          <div>
                            <div className="text-[#00ff4180]">HUMIDITY</div>
                            <div className="font-bold uppercase">{aiAnalysis.environmentalStatus.humidityStatus}</div>
                          </div>
                          <div>
                            <div className="text-[#00ff4180]">VPD</div>
                            <div className="font-bold uppercase">{aiAnalysis.environmentalStatus.vpdStatus}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {aiAnalysis.alerts && aiAnalysis.alerts.length > 0 && (
                      <div className="space-y-2 mb-3">
                        <h3 className="font-bold text-sm">ALERTS:</h3>
                        {aiAnalysis.alerts.map((alert, i) => (
                          <div key={i} className="bg-[#ffa50030] border border-[#ffa500] rounded p-2 flex items-start gap-2 text-xs">
                            <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
                            </svg>
                            <span>{alert}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ✨ SPLIT TASKS INTO ENVIRONMENTAL AND VISUAL */}
                    {aiAnalysis.userTasks && aiAnalysis.userTasks.length > 0 && (
                      <div className="space-y-3">
                        {/* Environmental Tasks */}
                        {aiAnalysis.userTasks.filter(t => t.source !== 'visual_inspection' && t.source !== 'training_recommendation').length > 0 && (
                          <>
                            <h3 className="font-bold text-sm flex items-center gap-2 text-[#00ff41]">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
                              </svg>
                              ENVIRONMENTAL TASKS:
                            </h3>
                            {aiAnalysis.userTasks
                              .filter(t => t.source !== 'visual_inspection' && t.source !== 'training_recommendation')
                              .map((task, i) => (
                                <div key={i} className={`border rounded p-2 text-xs ${
                                  task.priority === 'high' ? 'priority-high' : 
                                  task.priority === 'medium' ? 'priority-medium' : 
                                  'border-[#00ff41] bg-[#00ff4110]'
                                }`}>
                                  <div className="flex items-start justify-between">
                                    <div className="font-bold">{task.task}</div>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                                      task.priority === 'high' ? 'bg-red-600' : 
                                      task.priority === 'medium' ? 'bg-[#ffa500]' : 'bg-[#00ff41] text-[#0a0e1a]'
                                    }`}>
                                      {task.priority?.toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="text-[11px] mt-1 text-[#00ff4180]">{task.reason}</div>
                                </div>
                              ))}
                          </>
                        )}

                        {/* Visual Inspection Tasks */}
                        {aiAnalysis.userTasks.filter(t => t.source === 'visual_inspection' || t.source === 'training_recommendation').length > 0 && (
                          <>
                            <h3 className="font-bold text-sm flex items-center gap-2 text-[#9c27b0]">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                              </svg>
                              VISUAL INSPECTION TASKS:
                            </h3>
                            {aiAnalysis.userTasks
                              .filter(t => t.source === 'visual_inspection' || t.source === 'training_recommendation')
                              .map((task, i) => (
                                <div key={i} className={`border rounded p-2 text-xs ${
                                  task.priority === 'high' || task.priority === 'critical' ? 'priority-high' : 
                                  task.priority === 'medium' ? 'priority-medium' : 
                                  'border-[#9c27b0] bg-[#9c27b020]'
                                }`}>
                                  <div className="flex items-start justify-between">
                                    <div className="font-bold">{task.task}</div>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                                      task.priority === 'high' || task.priority === 'critical' ? 'bg-red-600' : 
                                      task.priority === 'medium' ? 'bg-[#ffa500]' : 
                                      'bg-[#9c27b0] text-white'
                                    }`}>
                                      {task.priority?.toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="text-[11px] mt-1 text-[#00ff4180]">{task.reason}</div>
                                  {task.source === 'training_recommendation' && (
                                    <div className="text-[10px] mt-1 text-[#9c27b0]">🌿 TRAINING</div>
                                  )}
                                </div>
                              ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recent Decisions */}
              {decisions.length > 0 && (
                <div className="terminal-border rounded-lg p-4 scan-line">
                  <h2 className="text-lg font-bold mb-3 flex items-center gap-2 terminal-glow">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    RECENT DECISIONS
                  </h2>
                  <div className="space-y-2">
                    {decisions.slice(0, 5).reverse().map((decision, i) => (
                      <div key={i} className="border-l-2 border-[#00ff41] pl-3 py-2">
                        <div className="flex items-center justify-between mb-1">
                          <div className="text-[10px] text-[#00ff4180]">
                            {new Date(decision.timestamp).toLocaleString()}
                          </div>
                          {decision.applied && (
                            <span className="text-[10px] bg-[#00ff41] text-[#0a0e1a] px-1.5 py-0.5 rounded font-bold">APPLIED</span>
                          )}
                        </div>
                        <div className="text-sm font-bold">{decision.summary}</div>
                        {decision.confidence && (
                          <div className="text-[10px] text-[#00ff4180] mt-1">
                            CONFIDENCE: {decision.confidence}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </>
      )}

      {/* LEARNINGS TAB CONTENT */}
      {activeTab === 'learnings' && (
        <LearningsTab apiUrl={API_URL} />
      )}

        {/* DAILY REPORT TAB CONTENT */}
      {activeTab === 'report' && (
        <DailyReport apiUrl={API_URL} />
      )}
      
      {activeTab === 'visual' && (
        <VisualInspectTab apiUrl={API_URL} />
      )}

      {/* Footer - Always Visible */}
      <div className="mt-6 text-center text-xs text-[#00ff4180]">
        <div className="flex items-center justify-center gap-4">
          <span>AUTO-REFRESH: 5s</span>
          <span>•</span>
          <span>LAST UPDATE: {lastUpdate.toLocaleTimeString()}</span>
          <span>•</span>
          <span>{isConnected ? 'LIVE DATA' : 'DEMO MODE'}</span>
        </div>
      </div>
    </div>
  );
};

export default TerminalDashboard;
